import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Divider from '@material-ui/core/Divider';
import Collapse from '@material-ui/core/Collapse';
import IconExpandLess from '@material-ui/icons/ExpandLess';
import IconExpandMore from '@material-ui/icons/ExpandMore';
import SidebarMenuItemComponent from './SideBarMenuItemComponent';
import Zoom from '@material-ui/core/Zoom';
import { useState } from 'react';
import { ListItemButton } from '@mui/material';
import { EnterpriseTooltip } from '../EnterpriseTooltip';

export const SideBarMenuItemPropTypes = {
  name: PropTypes.string.isRequired,
  link: PropTypes.string,
  Icon: PropTypes.elementType,
  items: PropTypes.array,
  tooltipTitle: PropTypes.string,
  drawerOpened: PropTypes.bool,
};

type SideBarMenuItemPropTypes = PropTypes.InferProps<
  typeof SideBarMenuItemPropTypes
>;
type SideBarMenuItemPropsWithoutItems = Omit<SideBarMenuItemPropTypes, 'items'>;

export type SideBarMenuItemProps = SideBarMenuItemPropsWithoutItems & {
  items?: SideBarMenuItemProps[];
};

const useStyles = makeStyles(theme =>
  createStyles({
    menuItem: {
      color: '#fff',
      "& .MuiListItemIcon-root":{
        minWidth: "42px"
      },
      '&.active': {
        background: '#153654',
        color: '#13E7FF',
        '& .MuiListItemIcon-root': {
          color: '#13E7FF',
        },
        '&:hover .MuiListItemIcon-root': {
          color: '#fff',
        },
      },
      '&:hover': {
        background: '#355E84',
        color: '#fff',
      },
    },
    menuItemIcon: {
      color: '#fff',
      '&:hover': {
        color: '#fff',
      },
    },
    menuItemText: {
      fontSize: '1.2rem',
    },
  })
);

const AppMenuItem: React.FC<SideBarMenuItemProps> = props => {
  const { name, link, Icon, items = [], tooltipTitle, drawerOpened } = props;
  const classes = useStyles();
  const isExpandable = items && items.length > 0;
  const [open, setOpen] = useState(false);

  function handleClick() {
    setOpen(!open);
  }

  const MenuItemRoot = (
    <React.Fragment>
      <SidebarMenuItemComponent
        className={classes.menuItem}
        link={link}
        onClick={handleClick}
      >
        {Icon &&
          (!drawerOpened ? (
            <>
              <EnterpriseTooltip
                title={tooltipTitle || ''}
                placement="right"
                TransitionComponent={Zoom}
              >
                <ListItemIcon className={classes.menuItemIcon}>
                  <Icon style={{ fontSize: 20 }} />
                </ListItemIcon>
              </EnterpriseTooltip>
            </>
          ) : (
            <>
              <ListItemIcon className={classes.menuItemIcon}>
                <Icon style={{ fontSize: 20 }} />
              </ListItemIcon>
            </>
          ))}
        <ListItemText
          primary={name}
          inset={!Icon}
          className={classes.menuItemText}
        />
        {/* Display the expand menu if the item has children */}
        {isExpandable && !open && <IconExpandMore />}
        {isExpandable && open && <IconExpandLess />}
      </SidebarMenuItemComponent>
    </React.Fragment>
  );

  const MenuItemChildren = isExpandable ? (
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Divider />
      <List component="div" disablePadding>
        {items.map((item, index) => (
          <ListItemButton sx={{ pl: 2.5, py: 0 }} disableGutters key={index}>
            <AppMenuItem {...item} key={index} />
          </ListItemButton>
        ))}
      </List>
    </Collapse>
  ) : null;

  return (
    <>
      {MenuItemRoot}
      {MenuItemChildren}
    </>
  );
};

export default AppMenuItem;
